import { html } from 'lit-element';

import { LightLitElement } from '@elab/components';
import connect from '@elab/core/store/connect';
import { selectors } from '@elab/core/store/user';
import { loadAsset } from '@elab/core/utils/helpers';

/**
 * Component is used to generate barcode.
 * JsBarcode is a barcode generator written in JavaScript.
 *
 * Link to documentation: https://github.com/lindell/JsBarcode
 *
 * @author: Bohdan Serhushev
 *
 * Main attributes:
 *
 * @attr {String} [barcode-id] - Should be the same as on <img id=""/>. Used as selector.
 * @attr {String} [barcode-format] - Select which barcode type to use
 * @attr {String} [barcode-height] - Height of barcode
 * @attr {String} [barcode-width] - Width of barcode
 * @attr {String} [background-color] - Background color of barcode
 * @attr {String} [line-color] - Line color of barcode
 * @attr {Boolean} [hide-number] - Define should number of barcode be hidden
 * @attr {Boolean} [show-full-card-number] - Define should show 13 digit barcode
 * Other options also can be added if need.
 *
 * HTML Structure
 * <!-- Example -->
 *
 * <e2-barcode class="loyalty-card__barcode"
 *             barcode-id="barcode"
 *             barcode-height="50">
 *      <img id="barcode"/>
 * </e2-barcode>
 */

export class Barcode extends LightLitElement {
    static get properties() {
        return {
            barcodeId: {
                type: String,
                attribute: 'barcode-id',
            },
            barcodeFormat: {
                type: String,
                attribute: 'barcode-type',
            },
            barcodeWidth: {
                type: Number,
                attribute: 'barcode-width',
            },
            barcodeHeight: {
                type: Number,
                attribute: 'barcode-height',
            },
            barcodeNumber: {
                type: String,
                attribute: false,
            },
            slotted: {
                type: Boolean,
                attribute: false,
            },
            backgroundColor: {
                type: String,
                attribute: 'background-color',
            },
            lineColor: {
                type: String,
                attribute: 'line-color',
            },
            hideNumber: {
                type: Boolean,
                attribute: 'hide-number',
            },
            showFullCardNumber: {
                type: Boolean,
                attribute: 'show-full-card-number',
            },
        };
    }

    constructor() {
        super();
        this.barcodeId = 'barcode';
        this.barcodeFormat = 'ean13';
        this.barcodeWidth = 2;
        this.barcodeHeight = 40;
        this.barcodeNumber = '';
        this.slotted = false;
        this.hideNumber = false;
        this.backgroundColor = 'white';
        this.lineColor = 'black';
        this.showFullCardNumber = false;
    }

    render() {
        return html`
            <v-slot></v-slot>
        `;
    }

    firstUpdated() {
        this.__slot = this.querySelector('v-slot');
        this.__slot.addEventListener('v::slotchange', this.handleSlotChange);
    }

    updated() {
        if (this.slotted && this.barcodeNumber) {
            this.initBarcode();
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        if (this.__slot) {
            this.__slot.removeEventListener('v::slotchange', this.handleSlotChange);
        }
    }

    handleSlotChange = () => {
        this.slotted = true;
    };

    async initBarcode() {
        const barcodeSelector = `#${this.barcodeId}`;
        await loadAsset('barcode');

        window.JsBarcode(barcodeSelector, this.barcodeNumber, {
            format: this.barcodeFormat,
            width: this.barcodeWidth,
            height: this.barcodeHeight,
            displayValue: !this.hideNumber,
            background: this.backgroundColor,
            lineColor: this.lineColor,
        });
    }
}

const mapStateToProps = (state, ctx) => {
    const { cardNumber, fullCardNumber } = selectors.getLoyaltyInformation(state);
    return {
        barcodeNumber: ctx.showFullCardNumber && fullCardNumber ? fullCardNumber : cardNumber,
    };
};

export default () => customElements.define('e2-barcode', connect(mapStateToProps)(Barcode));
